import { Command, Extension } from '@tiptap/core'
import { Node } from 'prosemirror-model'
import { AllSelection, TextSelection, Transaction } from 'prosemirror-state'

type IndentOptions = {
    types: string[];
    defaultLevel: number;

    minLevel: number;
    maxLevel: number;

    HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        indent: {
            /**
             * Set the indent attribute
             */
            indent: () => ReturnType;
            /**
             * Unset the indent attribute
             */
            outdent: () => ReturnType;
        };
    }
}

export function clamp(val: number, min: number, max: number): number {
    return Math.max(Math.min(max, val), min)
}

export enum IndentProps {
    more = 1,
    less = -1
}

export function isBulletListNode(node: Node): boolean {
    return node.type.name === 'bullet_list'
}

export function isOrderedListNode(node: Node): boolean {
    return node.type.name === 'order_list'
}

export function isTodoListNode(node: Node): boolean {
    return node.type.name === 'todo_list'
}

export function isListNode(node: Node): boolean {
    return isBulletListNode(node) ||
        isOrderedListNode(node) ||
        isTodoListNode(node)
}

export function setNodeIndentMarkup(tr: Transaction, pos: number, delta: number, minIndent: number, maxIndent: number): Transaction {
    if (!tr.doc) return tr

    const node = tr.doc.nodeAt(pos)
    if (!node) return tr

    const indent = clamp(
        (node.attrs.indent || 0) + delta,
        minIndent,
        maxIndent
    )

    if (indent === node.attrs.indent) return tr

    const nodeAttrs = {
        ...node.attrs,
        indent
    }

    return tr.setNodeMarkup(pos, node.type, nodeAttrs, node.marks)
}

export function updateIndentLevel(tr: Transaction, options: IndentOptions, delta: number): Transaction {
    const {doc, selection} = tr

    if (!doc || !selection) return tr

    if (!(selection instanceof TextSelection || selection instanceof AllSelection)) {
        return tr
    }

    const {from, to} = selection

    doc.nodesBetween(from, to, (node, pos) => {
        const nodeType = node.type

        if (nodeType.name === 'paragraph' || nodeType.name === 'heading') {
            tr = setNodeIndentMarkup(tr, pos, delta, options.minLevel, options.maxLevel)
            return false
        }

        return !isListNode(node);
    })

    return tr
}

export function createIndentCommand(delta: number, options: IndentOptions): Command {
    return ({tr, state, dispatch}) => {
        const {selection} = state
        tr = tr.setSelection(selection)
        tr = updateIndentLevel(tr, options, delta)

        if (tr.docChanged) {
            dispatch && dispatch(tr)
            return true
        }

        return false
    }
}

// @ts-ignore
export const Indent = Extension.create<IndentOptions>({
    name: 'indent',

    priority: 99,

    addOptions() {
        return {
            types: ['heading', 'paragraph'],
            defaultLevel: 0,

            minLevel: 0,
            maxLevel: 8,
            HTMLAttributes: {}
        }
    },

    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    indent: {
                        default: this.options.defaultLevel,
                        parseHTML: element => {
                            return (parseInt(element.style.textIndent?.replace(/[e|m]+/g, '')) / 2) || this.options.defaultLevel
                        },
                        renderHTML: attributes => {
                            if (attributes.indent === 0) {
                                return {}
                            } else {
                                return {
                                    style: `text-indent: ${attributes.indent * 2}em`,
                                    // 'data-indent': attributes.indent
                                }
                            }
                        },
                    }
                }
            }
        ]
    },

    addCommands() {
        return {
            indent: () => createIndentCommand(IndentProps.more, this.options),
            outdent: () => createIndentCommand(IndentProps.less, this.options)
        }
    },

    addKeyboardShortcuts() {
        return {
            Tab: () => {
                // list中的tab indent统一过滤掉
                const { state ,view} = this.editor;
                const { selection } = state;
                const { from, to } = this.editor.state.selection;
                const resolvedPos = this.editor.state.doc.resolve(from);
                // 获取当前节点
                const currentNode = resolvedPos.node();

                // 获取当前节点的起始位置
                const nodeStart = resolvedPos.start(resolvedPos.depth);
                const parentNode = resolvedPos.node(resolvedPos.depth - 1);

                console.log(parentNode,'--iii');
                if(currentNode.type.name==='codeBlock'){
                    const textBefore = resolvedPos.parent.textBetween(0, resolvedPos.parentOffset, '\n', '\n')
                    const lineBreakPos = textBefore.lastIndexOf('\n')
                    const lineStartPos = from - (textBefore.length - lineBreakPos - 1)
                    view.dispatch(
                        state.tr.insertText('\t', lineStartPos)
                    )
                }
                if (parentNode.type.name !== 'taskItem' && parentNode.type.name !== 'listItem') {
                    this.editor.commands.indent()
                    console.log('---1q---');
                    return true;
                } else {
                    console.log('---2q---');
                    return false
                }
            },
            'Shift-Tab': () => {
                const { state, view } = this.editor;
                const { from } = state.selection;
                const resolvedPos = state.doc.resolve(from);
                const currentNode = resolvedPos.node();

                if (currentNode.type.name === 'codeBlock') {
                    const textBefore = resolvedPos.parent.textBetween(0, resolvedPos.parentOffset, '\n', '\n')
                    const lineBreakPos = textBefore.lastIndexOf('\n')
                    const lineStartPos = from - (textBefore.length - lineBreakPos - 1)

                    const lineStartChar = state.doc.textBetween(lineStartPos, lineStartPos + 1)
                    if (lineStartChar === '\t') {
                        view.dispatch(state.tr.delete(lineStartPos, lineStartPos + 1))
                        return true
                    } else if (lineStartChar === ' ') {
                        let spacesToDelete = 0
                        for (let i = 0; i < 4; i++) {
                            const char = state.doc.textBetween(lineStartPos + i, lineStartPos + i + 1)
                            if (char === ' ') {
                                spacesToDelete++
                            } else {
                                break
                            }
                        }
                        if (spacesToDelete > 0) {
                            view.dispatch(state.tr.delete(lineStartPos, lineStartPos + spacesToDelete))
                            return true
                        }
                    }
                    return true
                }

                if (currentNode.type.name === 'paragraph' && currentNode.attrs?.indent > 0) {
                    this.editor.commands.outdent()
                    return true
                } else {
                    return false
                }
            }
        }
    }
})
